"use client";

import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
// @ts-expect-error - react-range-slider-input doesn't have built-in types
import RangeSlider from "react-range-slider-input";
import { useMediaQuery } from "react-responsive";

import { useGlobalData } from "@/context/globalContext";
import "react-range-slider-input/dist/style.css";
import styles from "./styles.module.scss";
import { Box as MantineBox } from "@mantine/core";

type Props = {
  title: string;
  icon: React.ReactNode;
  options: Filter[];
  onSelect: (type: string, value: Filter[]) => void;
  currency?: string;
  searchMinPrice?: number | null;
  searchMaxPrice?: number | null;
};

const DropdownPrice = ({
  title,
  options,
  onSelect,
  currency = "USD",
  searchMinPrice,
  searchMaxPrice,
}: Props) => {
  const { exchangeRates = {} } = useGlobalData();
  const currencyRate = exchangeRates[currency] || 1;

  // SEK and DKK use higher max values due to their lower value relative to USD/EUR
  const isScandicCurrency = currency === "SEK" || currency === "DKK";
  const MAX_DISPLAY_VALUE = useMemo(() => (isScandicCurrency ? 30000 : 3000), [isScandicCurrency]);
  const MAX_ACTUAL_VALUE = Infinity;

  const searchBasedMin =
    searchMinPrice !== null && searchMinPrice !== undefined
      ? Math.floor((searchMinPrice * currencyRate) / 5) * 5
      : 0;
  const searchBasedMax =
    searchMaxPrice !== null && searchMaxPrice !== undefined
      ? Math.ceil((searchMaxPrice * currencyRate) / 5) * 5
      : MAX_ACTUAL_VALUE;

  // Check if user has an active price filter (not at default Infinity)
  const getFilterMax = () => {
    const val = options?.[0]?.value;
    if (!val) return null;
    const max = (val as string).split(",")[1];
    if (max === "Infinity" || !isFinite(parseFloat(max))) return null;
    return parseInt(max, 10);
  };
  const filterMax = getFilterMax();
  const hasActivePriceFilter = filterMax !== null;

  // Always use fixed scale of 0-3000 regardless of search results
  const sliderMax = MAX_DISPLAY_VALUE;
  const sliderStep = Math.max(1, Math.floor(sliderMax / 100));

  // Check if value is "at max" (within one step of sliderMax, since step may not divide evenly)
  const isAtSliderMax = (value: number) => value >= sliderMax - sliderStep;

  const isMobile = useMediaQuery({ query: "(max-width: 1250px)" });
  const sliderRef = useRef<typeof RangeSlider>(null);
  const isDragging = useRef(false);

  const parseInitialValue = useCallback(
    (index: number, defaultVal: number) => {
      const val = options?.[0]?.value;
      if (!val) return defaultVal;
      const parsed = parseInt((val as string).split(",")[index]);
      return isNaN(parsed) ? defaultVal : parsed;
    },
    [options]
  );

  const [lowerValue, setLowerValue] = useState(() => parseInitialValue(0, searchBasedMin));
  const [higherValue, setHigherValue] = useState(() => parseInitialValue(1, searchBasedMax));
  const [prevCurrency, setPrevCurrency] = useState(currency);

  // Scale price values when switching between Scandic and other currencies
  useEffect(() => {
    if (prevCurrency === currency) return; // Skip initial mount

    const wasScandicCurrency = prevCurrency === "SEK" || prevCurrency === "DKK";
    const isNowScandicCurrency = currency === "SEK" || currency === "DKK";

    // Check if we were at max with the OLD currency scale
    const oldMaxDisplayValue = wasScandicCurrency ? 30000 : 3000;
    const oldSliderStep = Math.max(1, Math.floor(oldMaxDisplayValue / 100));
    const wasAtMax = higherValue >= oldMaxDisplayValue - oldSliderStep;

    // If switching from Scandic to non-Scandic, divide by 10
    if (wasScandicCurrency && !isNowScandicCurrency) {
      const newLower = Math.floor(lowerValue / 10);
      const newHigher = wasAtMax ? MAX_DISPLAY_VALUE : Math.floor(higherValue / 10);
      setLowerValue(newLower);
      setHigherValue(newHigher);
      // Commit the change with the new scaled values
      const actualHigh = wasAtMax ? MAX_ACTUAL_VALUE : newHigher;
      onSelect("price", [
        {
          id: 0,
          included: true,
          label: "price",
          state: "include",
          value: `${newLower},${actualHigh}`,
        },
      ]);
    }
    // If switching from non-Scandic to Scandic, multiply by 10
    else if (!wasScandicCurrency && isNowScandicCurrency) {
      const newLower = Math.floor(lowerValue * 10);
      const newHigher = wasAtMax ? MAX_DISPLAY_VALUE : Math.floor(higherValue * 10);
      setLowerValue(newLower);
      setHigherValue(newHigher);
      // Commit the change with the new scaled values
      const actualHigh = wasAtMax ? MAX_ACTUAL_VALUE : newHigher;
      onSelect("price", [
        {
          id: 0,
          included: true,
          label: "price",
          state: "include",
          value: `${newLower},${actualHigh}`,
        },
      ]);
    }

    setPrevCurrency(currency);
  }, [
    currency,
    prevCurrency,
    lowerValue,
    higherValue,
    onSelect,
    MAX_DISPLAY_VALUE,
    MAX_ACTUAL_VALUE,
  ]);

  useEffect(() => {
    // Only update from options if currency hasn't changed
    if (prevCurrency === currency) {
      setLowerValue(parseInitialValue(0, searchBasedMin));
      setHigherValue(parseInitialValue(1, searchBasedMax));
    }
  }, [options, searchBasedMin, searchBasedMax, prevCurrency, currency, parseInitialValue]);

  useEffect(() => {
    sliderRef?.current?.updater?.resizeEvent?.();
  }, [isMobile]);

  const commitPriceChange = (low: number, high: number) => {
    const actualHigh = isAtSliderMax(high) ? MAX_ACTUAL_VALUE : high;
    onSelect("price", [
      { id: 0, included: true, label: "price", state: "include", value: `${low},${actualHigh}` },
    ]);
  };

  const handleSliderInput = (values: [number, number]) => {
    setLowerValue(values[0]);
    setHigherValue(values[1]);
  };

  const handleSliderDragStart = () => {
    isDragging.current = true;
  };

  const handleSliderDragEnd = () => {
    isDragging.current = false;

    // Get current filter values
    const currentLow = parseInitialValue(0, searchBasedMin);
    const currentHigh = parseInitialValue(1, searchBasedMax);
    const actualHigherValue = isAtSliderMax(higherValue) ? MAX_ACTUAL_VALUE : higherValue;

    // Skip if values haven't changed
    if (lowerValue === currentLow && actualHigherValue === currentHigh) {
      return;
    }

    // Only reset to Infinity if there's no active price filter and slider is at default
    // If there's already an active filter, keep the actual value instead of resetting
    const isAtDefault = lowerValue === 0 && isAtSliderMax(higherValue);
    if (isAtDefault && !hasActivePriceFilter) {
      onSelect("price", [
        { id: 0, included: true, label: "$0 to Max", value: `0,${MAX_ACTUAL_VALUE}` },
      ]);
    } else {
      commitPriceChange(lowerValue, higherValue);
    }
  };

  const handleLowerValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (isNaN(value) || value < 0) return;
    const clamped = Math.min(value, sliderMax);
    setLowerValue(clamped);
  };

  const handleLowerValueBlur = () => {
    if (lowerValue > higherValue) {
      setLowerValue(higherValue);
      commitPriceChange(higherValue, higherValue);
    } else {
      commitPriceChange(lowerValue, higherValue);
    }
  };

  const handleHigherValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (isNaN(value) || value < 0) return;
    const clamped = Math.min(value, sliderMax);
    setHigherValue(clamped);
  };

  const handleHigherValueBlur = () => {
    if (higherValue < lowerValue) {
      setHigherValue(lowerValue);
      commitPriceChange(lowerValue, lowerValue);
    } else {
      commitPriceChange(lowerValue, higherValue);
    }
  };

  const isMax = isAtSliderMax(higherValue);
  const displayHigherValue = isMax ? "" : higherValue;
  const placeholderText = isMax ? (isScandicCurrency ? "30,000+" : "3,000+") : "MAX";

  return (
    <MantineBox>
      <MantineBox className={styles.header}>{title}</MantineBox>
      <MantineBox component="ul" className={styles.priceFilter}>
        <MantineBox component="li" className={styles.dropdownOptionPrice}>
          <MantineBox className={styles.priceInput}>
            <MantineBox
              component="input"
              type="number"
              value={lowerValue}
              min={0}
              max={sliderMax}
              onChange={handleLowerValueChange}
              onBlur={handleLowerValueBlur}
            />
            <MantineBox
              component="input"
              type="number"
              value={displayHigherValue}
              placeholder={placeholderText}
              min={0}
              max={sliderMax}
              onChange={handleHigherValueChange}
              onBlur={handleHigherValueBlur}
            />
          </MantineBox>
          <RangeSlider
            min={0}
            max={sliderMax}
            step={sliderStep}
            value={[lowerValue, Math.min(higherValue, sliderMax)]}
            className={styles.rangeSlider}
            onInput={handleSliderInput}
            onThumbDragStart={handleSliderDragStart}
            onThumbDragEnd={handleSliderDragEnd}
            ref={sliderRef}
          />
        </MantineBox>
      </MantineBox>
    </MantineBox>
  );
};

export default DropdownPrice;
